"use client";

import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Les icônes d'apps mélangent Solar et simple-icons (Plex pour le streaming),
// d'où le rendu via Icon d'Iconify avec l'identifiant complet de la collection.
const APPS = [
    { key: "cloud", icon: "solar:folder-cloud-bold", labelKey: "Nas_apps_app_cloud", tile: "bg-teal-500" },
    { key: "photos", icon: "solar:gallery-bold-duotone", labelKey: "Nas_apps_app_photos", tile: "bg-sky-500" },
    { key: "stream", icon: "simple-icons:plex", labelKey: "Nas_apps_app_stream", tile: "bg-amber-500" },
    { key: "surveillance", icon: "solar:videocamera-record-bold", labelKey: "Nas_apps_app_surveillance", tile: "bg-red-500" },
    { key: "backup", icon: "solar:shield-check-bold-duotone", labelKey: "Nas_apps_app_backup", tile: "bg-emerald-500" },
    { key: "office", icon: "solar:documents-bold", labelKey: "Nas_apps_app_office", tile: "bg-indigo-500" },
];

export default function NasComposer() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const [on, setOn] = useState({ cloud: true, photos: true });

    function toggle(key) {
        setOn((s) => ({ ...s, [key]: !s[key] }));
    }

    const enabled = APPS.filter((a) => on[a.key]);

    return (
        <section id="nas-composer" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Nas_apps_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Nas_apps_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-teal-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="server-bold-duotone"
                            className="w-3.5 h-3.5 text-teal-300"
                        />
                        DSM
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Nas_apps_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8 grid lg:grid-cols-2 gap-8 items-center">
                    {/* Contrôles */}
                    <div className="min-w-0 order-2 lg:order-1">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-300/90 mb-3">
                            {t("Nas_apps_hint")}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2.5">
                            {APPS.map((a) => {
                                const active = !!on[a.key];
                                return (
                                    <button
                                        key={a.key}
                                        type="button"
                                        onClick={() => toggle(a.key)}
                                        className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 ring-1 transition active:scale-95 ${
                                            active
                                                ? "bg-teal-500/15 ring-teal-400/50"
                                                : "bg-white/[0.04] ring-white/10 hover:bg-white/[0.07]"
                                        }`}
                                    >
                                        <span
                                            className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 text-white ${a.tile} ${active ? "" : "opacity-40 grayscale"}`}
                                        >
                                            <Icon icon={a.icon} className="w-5 h-5" aria-hidden="true" />
                                        </span>
                                        <span
                                            className={`flex-1 text-left text-[13px] font-semibold ${active ? "text-white" : "text-gray-400"}`}
                                        >
                                            {t(a.labelKey)}
                                        </span>
                                        <SolarIcon
                                            name={active ? "check-circle-bold" : "add-circle-bold"}
                                            className={`w-5 h-5 shrink-0 ${active ? "text-teal-400" : "text-gray-500"}`}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Bureau du NAS */}
                    <div className="min-w-0 order-1 lg:order-2">
                        <div className="rounded-2xl bg-slate-950 ring-1 ring-white/15 shadow-2xl shadow-black/50 p-2.5">
                            <div className="relative rounded-xl bg-gradient-to-br from-teal-900/50 via-slate-900 to-slate-950 overflow-hidden min-h-[300px]">
                                <div className="flex items-center justify-between px-4 py-2 text-[10px] text-white/50 border-b border-white/10">
                                    <span className="inline-flex items-center gap-1.5">
                                        <SolarIcon name="server-bold-duotone" className="w-3.5 h-3.5 text-teal-300" />
                                        DiskStation
                                    </span>
                                    <span>9:41</span>
                                </div>
                                <div className="p-5">
                                    {enabled.length === 0 ? (
                                        <div className="min-h-[210px] flex flex-col items-center justify-center text-center gap-2">
                                            <SolarIcon name="widget-bold" className="w-8 h-8 text-white/30" />
                                            <span className="text-[12px] text-white/40 px-6">
                                                {t("Nas_apps_empty")}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-3 gap-4">
                                            <AnimatePresence>
                                                {enabled.map((a) => (
                                                    <motion.div
                                                        key={a.key}
                                                        layout
                                                        initial={reduce ? false : { opacity: 0, scale: 0.6 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        exit={{ opacity: 0, scale: 0.6 }}
                                                        transition={{ type: "spring", stiffness: 380, damping: 22 }}
                                                        className="flex flex-col items-center gap-1.5"
                                                    >
                                                        <span
                                                            className={`flex items-center justify-center w-12 h-12 rounded-2xl text-white shadow-lg ${a.tile}`}
                                                        >
                                                            <Icon icon={a.icon} className="w-6 h-6" aria-hidden="true" />
                                                        </span>
                                                        <span className="text-[10px] text-white/80 text-center leading-tight">
                                                            {t(a.labelKey)}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Nas_apps_caption")}
            </p>
        </section>
    );
}
